import React, { forwardRef, useImperativeHandle } from "react"
import { View, Text } from "./primitives"

type PermissionStatus = "granted" | "denied" | "not-determined" | "restricted"

async function queryMediaPermission(kind: "camera" | "microphone"): Promise<PermissionStatus> {
  if (typeof navigator === "undefined") return "denied"
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: kind as PermissionName })
      if (status.state === "granted") return "granted"
      if (status.state === "denied") return "denied"
    }
  } catch {
    // permissions.query may fail for microphone on some browsers
  }
  return "not-determined"
}

async function requestMediaPermission(constraints: MediaStreamConstraints): Promise<PermissionStatus> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return "denied"
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    stream.getTracks().forEach((track) => track.stop())
    return "granted"
  } catch {
    return "denied"
  }
}

const MOCK_DEVICE = {
  id: "web-camera",
  position: "back" as const,
  hasFlash: false,
  hasTorch: false,
  isMultiCam: false,
  supportsFocus: false,
  minFocusDistance: 0,
  neutralZoom: 1,
  maxZoom: 1,
  formats: [],
}

export type CameraRef = {
  takePhoto?: (opts?: unknown) => Promise<{ path: string }>
  startRecording?: (opts: unknown) => Promise<void>
  stopRecording?: () => Promise<void>
}

const CameraComponent = forwardRef<
  CameraRef,
  {
    device?: unknown
    isActive?: boolean
    photo?: boolean
    video?: boolean
    audio?: boolean
    preview?: boolean
    enableZoomGesture?: boolean
    photoQualityBalance?: string
    codeScanner?: unknown
    style?: unknown
  }
>(function Camera(_props, ref) {
  useImperativeHandle(ref, () => ({
    takePhoto: async () => ({ path: "" }),
    startRecording: async () => {},
    stopRecording: async () => {},
  }))

  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        background: "#111",
        minHeight: 200,
      }}
    >
      <Text style={{ color: "#fff", fontSize: 14 }}>摄像头录制请在 App 中使用</Text>
    </View>
  )
})

export const Camera = Object.assign(CameraComponent, {
  getCameraPermissionStatus: () => queryMediaPermission("camera"),
  getMicrophonePermissionStatus: () => queryMediaPermission("microphone"),
  requestCameraPermission: () => requestMediaPermission({ video: true }),
  requestMicrophonePermission: () => requestMediaPermission({ audio: true }),
})

export const useCameraDevice = (_position?: string) => MOCK_DEVICE

export const useCodeScanner = (_opts?: unknown) => undefined
